// ============================================================================
// L'ÉTAPE « À QUI » DE L'ASSISTANT DE CAMPAGNE — une audience COMPOSÉE : Période · Inclure · Sauf.
// Réf. maquette du back-office (`MKCRIT`, `mkAudVals`), brief `BRIEF-Audiences-Marketing`.
//
// ⚠️ LA RÈGLE QUE L'ÉCRAN DIT, ET QUE LE SERVEUR APPLIQUE : tous les critères INCLUS sont vrais à la fois (ET) ;
//    un seul critère EXCLU suffit pour être écarté ; les critères d'achat se lisent sur la PÉRIODE OBSERVÉE.
//    L'écran ne calcule personne — il compose une question, le serveur la résout et compte.
// ⚠️ UN CRITÈRE SANS VALEUR EST « À COMPLÉTER » ET NE VOYAGE PAS : il ne filtrerait rien, et la trace de l'envoi
//    porterait un critère que personne n'a posé.
// ⚠️ ON GARDE LA QUESTION, JAMAIS LA LISTE : une période choisie par raccourci part en RELATIF (« 12 derniers
//    mois »), pour qu'une audience enregistrée ou un modèle récurrent la rejoue au jour où ils servent.
// ============================================================================
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, Switch, Trans } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { currentCurrency, formatMoney, parseMinor, toInputValue } from '@orgazone/money-format';
import {
  api, type ArticleFacets, type CampaignBoughtDimension, type CampaignFilters, type CampaignPlace,
  type CampaignPurchasePeriod, type CampaignRelativeSpan, type SavedAudience,
} from '../api';
import { key, t, type DeclaredKey } from '../i18n';
import { currentLang } from '../lang';
import { fieldLabel } from '../vocabulary';
import { TYPE_TITRE } from './clients-kit';
import { rfmSegmentLabel } from '../rfm-segments';
import { POP_HEAD, StatsPopover } from './stats-bar';
import { monthsBefore, purchaseWindowOf, spanMonths } from '../../../server/src/marketing/audience-periods';

// ─── Le modèle de l'écran ────────────────────────────────────────────────────────────────────────

/** Les critères que l'étape propose — leurs clés sont celles de la maquette. */
export type CleCritere =
  | 'pal' | 'seg' | 'cag' | 'tit' | 'vis' | 'dorm' | 'nouv' | 'ca' | 'pm'
  | 'aMarq' | 'aFam' | 'aSup' | 'aSais' | 'anniv' | 'geo';

/** Un nombre de visites : au moins, au plus — en texte, tel que saisi. */
export interface Plage { min: string; max: string }

/** La valeur d'un critère : un choix, une liste, un montant ou une durée saisis, ou une plage. */
export type ValeurCritere = string | string[] | Plage;

/** Un critère posé, avec sa valeur — vide tant qu'il est « à compléter ». */
export interface Critere { k: CleCritere; v: ValeurCritere }

/** Un raccourci de période : relatif, ou « toute la vie du client » (aucune fenêtre). */
export type RaccourciPeriode = CampaignPurchasePeriod | 'all';

/** La période observée : un raccourci, ou deux dates `AAAA-MM-JJ` choisies. */
export type Periode = { q: RaccourciPeriode } | { f: string; t: string };

/** L'audience telle que l'écran la compose. */
export interface Criteres { per: Periode; inc: Critere[]; exc: Critere[] }

/** Une audience au repos — 12 derniers mois, tous les clients joignables, personne d'écarté. */
export const CRITERES_VIDES: Criteres = { per: { q: '12m' }, inc: [], exc: [] };

type Zone = 'inc' | 'exc';
type Ty = 'one' | 'many' | 'eur' | 'range' | 'date';
type Groupe = 'fid' | 'act' | 'ach' | 'pro';
type Source = 'pal' | 'seg' | 'tit' | 'mois' | 'marq' | 'fam' | 'sup' | 'sais' | 'geo';

interface DefCritere {
  k: CleCritere;
  g: Groupe;
  ty: Ty;
  ic: IconName;
  /** Le nom au menu, puis le nom court de la pastille — lus AU RENDU (langue du compte, mot du magasin). */
  l: () => string;
  s: () => string;
  /** Le critère lit la période observée. */
  per?: true;
  src?: Source;
  /** Les durées proposées d'un critère daté. */
  quick?: readonly { v: CampaignRelativeSpan; l: DeclaredKey }[];
  /** L'intitulé de sa date choisie. */
  dl?: DeclaredKey;
  note?: DeclaredKey;
}

const GROUPES: readonly { g: Groupe; l: DeclaredKey }[] = [
  { g: 'fid', l: key('Fidélité') }, { g: 'act', l: key('Activité') }, { g: 'ach', l: key('Achats') }, { g: 'pro', l: key('Profil') },
];

/**
 * « A ACHETÉ… » NOMME LE CHAMP DANS LE MOT DU MAGASIN : un caviste lit « Millésime » là où le dessin dit « Saison »
 * (même règle que les filtres des statistiques). La dimension voyage telle quelle : c'est la colonne de l'article.
 */
const ACHAT: Record<'aMarq' | 'aFam' | 'aSup' | 'aSais', { dim: CampaignBoughtDimension; champ: 'brand' | 'family' | 'supplier' | 'season'; ic: IconName }> = {
  aMarq: { dim: 'marq', champ: 'brand', ic: 'tag' },
  aFam: { dim: 'fam', champ: 'family', ic: 'folder' },
  aSup: { dim: 'sup', champ: 'supplier', ic: 'truck' },
  aSais: { dim: 'sais', champ: 'season', ic: 'sun' },
};
const achatDef = (k: keyof typeof ACHAT): DefCritere => ({
  k, g: 'ach', ty: 'many', ic: ACHAT[k].ic, per: true, src: ACHAT[k].dim,
  l: () => t('A acheté — {champ}', { champ: fieldLabel(ACHAT[k].champ) }),
  s: () => t('Achat · {champ}', { champ: fieldLabel(ACHAT[k].champ) }),
});

/** LES CRITÈRES, dans l'ordre du menu — une seule table, lue par le menu, les pastilles et l'aller-retour. */
export const CRITERES: readonly DefCritere[] = [
  { k: 'pal', g: 'fid', ty: 'one', ic: 'star', src: 'pal', l: () => t('Palier de fidélité'), s: () => t('Palier') },
  {
    k: 'seg', g: 'fid', ty: 'many', ic: 'pie', src: 'seg', l: () => t('Segment RFM'), s: () => t('Segment'),
    note: key('Calculé par le serveur sur les deux dernières années d’achats — sous un nombre minimal d’acheteurs, il refuse et dit pourquoi.'),
  },
  { k: 'cag', g: 'fid', ty: 'eur', ic: 'coin', l: () => t('Cagnotte minimum'), s: () => t('Cagnotte') },
  {
    k: 'tit', g: 'fid', ty: 'many', ic: 'ticketTag', src: 'tit', l: () => t('Détient un titre non utilisé'), s: () => t('Titre non utilisé'),
    note: key('Titre encore valide et pas encore consommé.'),
  },
  { k: 'vis', g: 'act', ty: 'range', ic: 'store', per: true, l: () => t('Nombre de visites'), s: () => t('Visites') },
  {
    k: 'dorm', g: 'act', ty: 'date', ic: 'moon', l: () => t('Pas venu depuis'), s: () => t('Pas venu depuis'),
    quick: [{ v: '3m', l: key('3 mois') }, { v: '6m', l: key('6 mois') }, { v: '12m', l: key('1 an') }, { v: '24m', l: key('2 ans') }],
    dl: key('Aucune visite depuis le'),
  },
  {
    k: 'nouv', g: 'act', ty: 'date', ic: 'userPlus', l: () => t('Nouveau client'), s: () => t('Fiche créée depuis'),
    quick: [{ v: '1m', l: key('1 mois') }, { v: '3m', l: key('3 mois') }, { v: '6m', l: key('6 mois') }, { v: '12m', l: key('1 an') }],
    dl: key('Fiche créée depuis le'),
  },
  { k: 'ca', g: 'ach', ty: 'eur', ic: 'euro', per: true, l: () => t('Achats minimum'), s: () => t('Achats') },
  { k: 'pm', g: 'ach', ty: 'eur', ic: 'basket', per: true, l: () => t('Panier moyen minimum'), s: () => t('Panier moyen') },
  achatDef('aMarq'), achatDef('aFam'), achatDef('aSup'), achatDef('aSais'),
  {
    k: 'anniv', g: 'pro', ty: 'many', ic: 'cake', src: 'mois', l: () => t('Mois d’anniversaire'), s: () => t('Anniversaire'),
    note: key('Plusieurs mois possibles (un trimestre = une campagne). Une fiche sans date de naissance n’entre jamais.'),
  },
  { k: 'geo', g: 'pro', ty: 'many', ic: 'pin', src: 'geo', l: () => t('Ville / code postal'), s: () => t('Ville / CP') },
];

const defDe = (k: CleCritere): DefCritere => CRITERES.find((d) => d.k === k)!;

/** « Gold et plus » RÉSOLU EN LISTE — l'ordre des paliers est un choix produit, pas l'alphabet. */
const PALIERS_ET_PLUS: Record<string, string[]> = {
  S: ['silver', 'gold', 'platinium', 'S', 'G', 'P'],
  G: ['gold', 'platinium', 'G', 'P'],
  P: ['platinium', 'P'],
};
const PALIERS: Record<string, DeclaredKey> = { S: key('Silver et plus'), G: key('Gold et plus'), P: key('Platinium') };

/**
 * LES TITRES qu'un client peut détenir — ceux de la maquette, sous les noms du registre des titres.
 * ⚠️ « Bon de campagne » n'existe pas : le dessin le proposait, aucun titre n'en porte le type. Le coupon de
 *    remise est le titre réel qui s'en approche, et il garde son nom.
 */
const TITRES = ['avoir', 'cadeau', 'fidelite', 'coupon'] as const;

/** Les raccourcis de période, dans l'ordre du dessin. */
const PERIODES: readonly { q: RaccourciPeriode; l: DeclaredKey }[] = [
  { q: '1m', l: key('Dernier mois') }, { q: '3m', l: key('3 derniers mois') }, { q: '6m', l: key('6 derniers mois') },
  { q: '12m', l: key('12 derniers mois') }, { q: '24m', l: key('24 derniers mois') }, { q: 'ytd', l: key('Cette année') },
  { q: 'ly', l: key('Année dernière') }, { q: 'all', l: key('Toute la vie du client') },
];

/**
 * LE DÉPARTEMENT d'un code postal — ses deux premiers caractères, trois outre-mer (971…976, 98…).
 * ⚠️ « 97 · tout le département » réunirait la Guadeloupe et La Réunion : le préfixe y est à trois chiffres.
 */
export const departementDe = (cp: string): string => (/^9[78]/.test(cp) ? cp.slice(0, 3) : cp.slice(0, 2));

// ─── Dates ───────────────────────────────────────────────────────────────────────────────────────

const versJour = (iso: string): string => {
  const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[1]}${m[2]}${m[3]}` : '';
};
const versIso = (ymd: string): string =>
  (/^\d{8}$/.test(ymd) ? `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}` : '');
const estIso = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v);
const DUREES: readonly string[] = ['1m', '3m', '6m', '12m', '24m'];
const estDuree = (v: string): v is CampaignRelativeSpan => DUREES.includes(v);

/** Aujourd'hui `AAAAMMJJ`, au calendrier de qui regarde — l'affichage seulement ; le serveur résout chez le magasin. */
const aujourdhui = (): string => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

/** Une date `AAAAMMJJ` lue dans la langue du compte. */
const dateLisible = (ymd: string): string => (/^\d{8}$/.test(ymd)
  ? new Intl.DateTimeFormat(currentLang(), { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)))))
  : '');

/**
 * LA FENÊTRE QUE L'ÉCRAN MONTRE pour une période — le même calcul que le serveur (`audience-periods`).
 * `null` = toute la vie du client, sans borne.
 */
export function fenetreDe(per: Periode, today = aujourdhui()): { from: string; to: string } | null {
  if ('q' in per) return per.q === 'all' ? null : purchaseWindowOf(per.q, today);
  const from = versJour(per.f);
  const to = versJour(per.t);
  return from && to ? { from, to } : null;
}

/** Le nom d'une période — le raccourci, ou « Dates personnalisées ». */
export const nomPeriode = (per: Periode): string =>
  ('q' in per ? t(PERIODES.find((p) => p.q === per.q)?.l ?? key('Dates personnalisées')) : t('Dates personnalisées'));

const plageLisible = (w: { from: string; to: string }): string =>
  (w.from === w.to ? dateLisible(w.from) : `${dateLisible(w.from)} → ${dateLisible(w.to)}`);

// ─── Ce qui est posé, ce qui voyage ──────────────────────────────────────────────────────────────

const montant = (v: ValeurCritere): number | undefined => {
  if (typeof v !== 'string') return undefined;
  const lu = parseMinor(v);
  return lu.state === 'ok' && lu.minor > 0 ? lu.minor : undefined;
};
const entier = (v: string, min: number): number | undefined => {
  const n = v.trim() === '' ? NaN : Number(v);
  return Number.isInteger(n) && n >= min ? n : undefined;
};
const plageDe = (v: ValeurCritere): Plage =>
  (typeof v === 'object' && !Array.isArray(v) ? v : { min: '', max: '' });

/** Le critère FILTRE-t-il ? Sans valeur il est « à compléter », et ne voyage pas. */
export function estPose(cr: Critere): boolean {
  const d = defDe(cr.k);
  const v = cr.v;
  if (d.ty === 'many') return Array.isArray(v) && v.length > 0;
  if (d.ty === 'range') {
    const p = plageDe(v);
    return entier(p.min, 1) !== undefined || entier(p.max, 0) !== undefined;
  }
  if (d.ty === 'eur') return montant(v) !== undefined;
  if (d.ty === 'date') return typeof v === 'string' && (estDuree(v) || estIso(v));
  return typeof v === 'string' && v !== '';
}

/** Un critère POSÉ, inclus ou exclu, lit-il la période observée ? Sinon la période n'est pas envoyée. */
export const utilisePeriode = (c: Criteres): boolean =>
  [...c.inc, ...c.exc].some((cr) => defDe(cr.k).per && estPose(cr));

/** Les critères posés d'une bande → leur forme serveur. */
function jeuDe(liste: readonly Critere[]): Omit<CampaignFilters, 'exclude' | 'purchasePeriod' | 'purchaseFrom' | 'purchaseTo'> {
  const f: Omit<CampaignFilters, 'exclude' | 'purchasePeriod' | 'purchaseFrom' | 'purchaseTo'> = {};
  for (const cr of liste) {
    if (!estPose(cr)) continue;
    const v = cr.v;
    const l = Array.isArray(v) ? v : [];
    const s = typeof v === 'string' ? v : '';
    switch (cr.k) {
      case 'pal': f.tierIn = PALIERS_ET_PLUS[s] ?? []; break;
      case 'seg': f.segments = l; break;
      case 'cag': f.minLoyaltyCents = montant(v); break;
      case 'tit': f.holdsTitleKinds = l; break;
      case 'vis': {
        const p = plageDe(v);
        const min = entier(p.min, 1);
        const max = entier(p.max, 0);
        if (min !== undefined) f.visitsMin = min;
        if (max !== undefined) f.visitsMax = max;
        break;
      }
      // ⚠️ UNE DURÉE PART EN DURÉE, une date en date : c'est la durée qu'un modèle doit rejouer.
      case 'dorm': if (estDuree(s)) f.notVisitedFor = s; else f.notVisitedSince = versJour(s); break;
      case 'nouv': if (estDuree(s)) f.createdWithin = s; else f.createdSince = versJour(s); break;
      case 'ca': f.minCaCents = montant(v); break;
      case 'pm': f.minAvgBasketCents = montant(v); break;
      case 'aMarq': case 'aFam': case 'aSup': case 'aSais':
        f.bought = [...(f.bought ?? []), { dim: ACHAT[cr.k].dim, values: l }];
        break;
      // ⚠️ LES MOIS PARTENT EN NOMBRES : le serveur écarte tout ce qui n'est pas un entier de 1 à 12.
      case 'anniv': f.birthMonths = l.map(Number); break;
      case 'geo': {
        const prefixes = l.filter((x) => x.startsWith('dep:')).map((x) => x.slice(4));
        const villes = l.filter((x) => x.startsWith('city:')).map((x) => {
          const [city = '', cp = ''] = x.slice(5).split('|');
          return { city, cp };
        });
        if (prefixes.length) f.postalPrefixes = prefixes;
        if (villes.length) f.cities = villes;
        break;
      }
    }
  }
  return f;
}

/**
 * CE QUE L'ÉTAPE DEMANDE AU SERVEUR.
 *
 * ⚠️ 🔴 PURE ET EXPORTÉE, parce que c'est ELLE qui décide qui reçoit un message facturé. La classe de défaut qui
 * la guette est muette : un critère saisi, compté à l'écran, puis JAMAIS TRANSMIS — l'audience part plus large
 * que ce que l'exploitant a demandé, sans qu'aucun écran ne mente visiblement.
 * ⚠️ LA PÉRIODE NE VOYAGE QUE SI UN CRITÈRE POSÉ LA LIT, et « toute la vie du client » ne voyage pas : c'est
 *    l'absence de fenêtre.
 */
export function filtresDe(c: Criteres): CampaignFilters {
  const inc = jeuDe(c.inc);
  const exc = jeuDe(c.exc);
  const fenetre: Pick<CampaignFilters, 'purchasePeriod' | 'purchaseFrom' | 'purchaseTo'> = {};
  if (utilisePeriode(c)) {
    if ('q' in c.per) {
      if (c.per.q !== 'all') fenetre.purchasePeriod = c.per.q;
    } else {
      const from = versJour(c.per.f);
      const to = versJour(c.per.t);
      if (from) fenetre.purchaseFrom = from;
      if (to) fenetre.purchaseTo = to;
    }
  }
  return { ...inc, ...fenetre, ...(Object.keys(exc).length ? { exclude: exc } : {}) };
}

/** Les critères d'un jeu serveur → les pastilles d'une bande, et ce qui ne s'y laisse pas rendre. */
function bandeDe(f: Omit<CampaignFilters, 'exclude'>, ignores: string[]): Critere[] {
  const par = new Map<CleCritere, ValeurCritere>();
  if (f.tierIn?.length) {
    const cle = Object.keys(PALIERS_ET_PLUS).find((k) => PALIERS_ET_PLUS[k]!.join('|') === f.tierIn!.join('|'));
    if (cle) par.set('pal', cle);
    else ignores.push(t('palier de fidélité'));
  }
  if (f.segments?.length) par.set('seg', [...f.segments]);
  if (typeof f.minLoyaltyCents === 'number') par.set('cag', toInputValue(f.minLoyaltyCents));
  if (f.holdsTitleKinds?.length) par.set('tit', [...f.holdsTitleKinds]);
  if (f.visitsMin !== undefined || f.visitsMax !== undefined) {
    par.set('vis', { min: f.visitsMin === undefined ? '' : String(f.visitsMin), max: f.visitsMax === undefined ? '' : String(f.visitsMax) });
  }
  if (f.notVisitedFor) par.set('dorm', f.notVisitedFor);
  else if (f.notVisitedSince) par.set('dorm', versIso(f.notVisitedSince));
  if (f.createdWithin) par.set('nouv', f.createdWithin);
  else if (f.createdSince) par.set('nouv', versIso(f.createdSince));
  if (typeof f.minCaCents === 'number') par.set('ca', toInputValue(f.minCaCents));
  if (typeof f.minAvgBasketCents === 'number') par.set('pm', toInputValue(f.minAvgBasketCents));
  for (const b of f.bought ?? []) {
    const k = (Object.keys(ACHAT) as (keyof typeof ACHAT)[]).find((x) => ACHAT[x].dim === b.dim);
    // ⚠️ DEUX ENTRÉES DE LA MÊME DIMENSION (« NIKE » ET « PUMA ») ne font pas une pastille « NIKE, PUMA » : la
    //    seconde est un OU, le serveur lisait un ET. On garde la première et on le dit.
    if (!k || par.has(k)) { ignores.push(t('achats d’une même dimension')); continue; }
    par.set(k, [...b.values]);
  }
  if (f.birthMonths?.length) par.set('anniv', f.birthMonths.map(String));
  if (f.postalPrefixes?.length || f.cities?.length) {
    par.set('geo', [
      ...(f.postalPrefixes ?? []).map((p) => `dep:${p}`),
      ...(f.cities ?? []).map((x) => `city:${x.city}|${x.cp}`),
    ]);
  }
  // ⚠️ CE QUE CET ÉCRAN NE SAIT PAS RENDRE — nommé, jamais avalé.
  if (f.qualifications?.length) ignores.push(t('qualifications'));
  if (f.withLoyaltyBalance) ignores.push(t('porteurs d’une cagnotte'));
  return CRITERES.filter((d) => par.has(d.k)).map((d) => ({ k: d.k, v: par.get(d.k)! }));
}

/**
 * RELIRE UNE AUDIENCE ENREGISTRÉE EN CRITÈRES D'ÉCRAN — et DIRE ce qu'on n'a pas su afficher.
 *
 * ⚠️ 🔴 LE DANGER N'EST PAS LA TRADUCTION, C'EST CE QU'ELLE PERD EN SILENCE. Une audience qui porte un critère que
 * l'écran ne rend pas, rechargée puis figée ici, partirait PLUS LARGE que ce qui avait été enregistré — sans
 * qu'aucun écran ne mente visiblement. La fonction rend donc ce qu'elle a ignoré, et l'écran le montre.
 * ⚠️ UNE AUDIENCE SANS FENÊTRE dont un critère d'achat est posé lisait TOUTE LA VIE du client : elle revient sur
 *    « Toute la vie du client », pas sur les 12 derniers mois par défaut.
 * ⚠️ L'ANCIENNE FENÊTRE DE VISITE (« venus sur une période ») devient la période observée avec « au moins une
 *    visite » — sauf si une autre période ou un autre critère d'achat la contredirait : elle est alors nommée.
 */
export function criteresDe(filters: CampaignFilters): { criteres: Criteres; ignores: string[] } {
  const ignores: string[] = [];
  const inc = bandeDe(filters, ignores);
  const exc = filters.exclude ? bandeDe(filters.exclude, ignores) : [];
  const fenetre: Periode | null = filters.purchasePeriod
    ? { q: filters.purchasePeriod }
    : filters.purchaseFrom || filters.purchaseTo
      ? { f: versIso(filters.purchaseFrom ?? ''), t: versIso(filters.purchaseTo ?? filters.purchaseFrom ?? '') }
      : null;
  const brouillon: Criteres = { per: CRITERES_VIDES.per, inc, exc };
  let per: Periode = fenetre ?? (utilisePeriode(brouillon) ? { q: 'all' } : CRITERES_VIDES.per);

  if (filters.visitedFrom || filters.visitedTo) {
    const libre = !fenetre && !utilisePeriode(brouillon) && !inc.some((x) => x.k === 'vis');
    if (libre && filters.visitedFrom && filters.visitedTo) {
      per = { f: versIso(filters.visitedFrom), t: versIso(filters.visitedTo) };
      inc.push({ k: 'vis', v: { min: '1', max: '' } });
      inc.sort((a, b) => CRITERES.indexOf(defDe(a.k)) - CRITERES.indexOf(defDe(b.k)));
    } else {
      ignores.push(t('fenêtre de visite'));
    }
  }
  return { criteres: { per, inc, exc }, ignores };
}

// ─── Ce qu'on lit d'un critère ───────────────────────────────────────────────────────────────────

/** Les valeurs offertes par un critère à liste, et les libellés qu'on sait déjà. */
export interface SourcesDeChoix {
  segments: string[] | null;
  facettes: ArticleFacets | null;
  lieux: CampaignPlace[] | null;
}

const moisDeLAnnee = (): string[] => {
  const f = new Intl.DateTimeFormat(currentLang(), { month: 'long' });
  return Array.from({ length: 12 }, (_, i) => {
    const nom = f.format(new Date(2000, i, 1));
    return nom.charAt(0).toUpperCase() + nom.slice(1);
  });
};

/** Les choix d'un critère à liste — `[valeur, libellé]`. */
function optionsDe(d: DefCritere, src: SourcesDeChoix): [string, string][] {
  switch (d.src) {
    case 'pal': return Object.keys(PALIERS).map((k) => [k, t(PALIERS[k]!)]);
    case 'seg': return (src.segments ?? []).map((s) => [s, rfmSegmentLabel(s)]);
    case 'tit': return TITRES.map((x) => [x, t(TYPE_TITRE[x]!.l)]);
    case 'mois': return moisDeLAnnee().map((m, i) => [String(i + 1), m]);
    case 'marq': case 'fam': case 'sup': case 'sais':
      return (src.facettes?.values[d.src] ?? []).map((v) => [v.value, v.value]);
    case 'geo': {
      const lieux = src.lieux ?? [];
      const deps = [...new Set(lieux.map((l) => departementDe(l.cp)))].sort();
      return [
        ...deps.map((dep): [string, string] => [`dep:${dep}`, t('{departement} · tout le département', { departement: dep })]),
        ...lieux.filter((l) => l.city).map((l): [string, string] => [`city:${l.city}|${l.cp}`, `${l.city} (${l.cp})`]),
      ];
    }
    default: return [];
  }
}

/** Le libellé d'une valeur posée, sans attendre la liste des choix. */
function libelleDeValeur(d: DefCritere, v: string): string {
  if (d.src === 'pal') return PALIERS[v] ? t(PALIERS[v]) : v;
  if (d.src === 'seg') return rfmSegmentLabel(v);
  if (d.src === 'tit') return TYPE_TITRE[v] ? t(TYPE_TITRE[v].l) : v;
  if (d.src === 'mois') return moisDeLAnnee()[Number(v) - 1] ?? v;
  if (d.src === 'geo') {
    if (v.startsWith('dep:')) return t('{departement} · tout le département', { departement: v.slice(4) });
    const [city = '', cp = ''] = v.slice(5).split('|');
    return `${city} (${cp})`;
  }
  return v;
}

/** Ce que dit la pastille d'un critère posé. */
export function valeurLisible(cr: Critere): string {
  const d = defDe(cr.k);
  const v = cr.v;
  if (d.ty === 'many') {
    const noms = (Array.isArray(v) ? v : []).map((x) => libelleDeValeur(d, x));
    return noms.slice(0, 2).join(', ') + (noms.length > 2 ? ` +${noms.length - 2}` : '');
  }
  if (d.ty === 'eur') return `≥ ${formatMoney(montant(v) ?? 0)}`;
  if (d.ty === 'range') {
    const p = plageDe(v);
    const min = entier(p.min, 1);
    const max = entier(p.max, 0);
    if (min !== undefined && max !== undefined) return t('{min} à {max}', { min, max });
    return max !== undefined ? t('au plus {max}', { max }) : t('au moins {min}', { min: min ?? 0 });
  }
  if (d.ty === 'date') {
    const s = typeof v === 'string' ? v : '';
    const q = d.quick?.find((x) => x.v === s);
    return q ? t(q.l) : dateLisible(versJour(s));
  }
  return libelleDeValeur(d, typeof v === 'string' ? v : '');
}

/**
 * « Palier : Gold et plus » — LA CLÉ DE LA PASTILLE, sans ses marques de mise en forme.
 * ⚠️ LA PHRASE DIT CE QUE LA PASTILLE DIT, dans toutes les langues : une seconde clé de la même forme se traduirait à
 *    part, et l'anglais lirait « Tier: Gold » sur la pastille et « Tier : Gold » dans la liste des audiences.
 */
const pastilleEnTexte = (critere: string, valeurs: string): string =>
  t('<1>{critere} :</1> <2>{valeurs}</2>', { critere, valeurs }).replace(/<\/?\d+>/g, '');

/**
 * LA PHRASE D'UNE AUDIENCE — « Palier : Gold et plus · Achats : ≥ 150,00 € (12 derniers mois) — sauf Titre non
 * utilisé : Avoir ». C'est ce qu'on relit dans la liste des audiences enregistrées.
 */
export function phraseAudience(c: Criteres): string {
  const periode = nomPeriode(c.per).toLocaleLowerCase(currentLang());
  const partie = (cr: Critere): string => {
    const d = defDe(cr.k);
    const texte = pastilleEnTexte(d.s(), valeurLisible(cr));
    return d.per ? `${texte} (${periode})` : texte;
  };
  const inclus = c.inc.filter(estPose).map(partie);
  const exclus = c.exc.filter(estPose).map(partie);
  const tete = inclus.length ? inclus.join(' · ') : t('Tous les clients');
  return exclus.length ? t('{inclus} — sauf {exclus}', { inclus: tete, exclus: exclus.join(', ') }) : tete;
}

// ─── L'écran ─────────────────────────────────────────────────────────────────────────────────────

/** Le libellé d'une bande — la grammaire de la barre de filtres des statistiques. */
const BANDE_NOM: CSSProperties = {
  width: 58, flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
  textTransform: 'uppercase', color: 'var(--ink-500)',
};
const BANDE: CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px 10px', flexWrap: 'wrap', padding: '10px 13px' };
const AJOUT: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', border: '1.5px dashed var(--ink-300)',
  borderRadius: 'var(--r-pill)', background: 'transparent', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700,
  whiteSpace: 'nowrap', cursor: 'pointer',
};
const VIDE: CSSProperties = { fontSize: 'var(--t-dense)', color: 'var(--ink-500)' };
const REGLE: CSSProperties = { marginLeft: 'auto', fontSize: 'var(--t-note)', color: 'var(--ink-400)', whiteSpace: 'nowrap' };
const CHAMP: CSSProperties = {
  width: '100%', height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', outline: 'none', boxSizing: 'border-box',
};
const SOUS_TITRE: CSSProperties = { display: 'block', fontSize: 'var(--t-micro)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 4 };
const PIED: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)',
};
const FERMER: CSSProperties = {
  marginLeft: 'auto', height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)',
  color: '#fff', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer',
};
const DISCRET: CSSProperties = {
  height: 30, padding: '0 9px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-600)',
  fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer',
};
const AMBRE: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 11px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)',
  color: 'var(--amber-fg)', fontSize: 'var(--t-dense)', fontWeight: 600, lineHeight: 1.4,
};

/** Un bouton de choix rapide (durée, période) : choisi ou non. */
const choix = (on: boolean): CSSProperties => ({
  height: 32, padding: '0 8px', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)',
  background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-700)', fontSize: 'var(--t-dense)',
  fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
});

/** Les couleurs d'une pastille : à compléter (ambre, pointillé), incluse (teal), exclue (rose). */
function teinte(pose: boolean, zone: Zone): CSSProperties {
  if (!pose) return { background: 'var(--amber-bg)', borderColor: 'var(--amber-fg)', borderStyle: 'dashed', color: 'var(--amber-fg)' };
  return zone === 'inc'
    ? { background: 'var(--teal-100)', borderColor: 'var(--teal-400)', borderStyle: 'solid', color: 'var(--teal-800)' }
    : { background: 'var(--rose-bg)', borderColor: 'var(--rose-fg)', borderStyle: 'solid', color: 'var(--rose-fg)' };
}

type Pop =
  | { kind: 'add'; zone: Zone; r: DOMRect }
  | { kind: 'edit'; zone: Zone; i: number; r: DOMRect }
  | { kind: 'per'; r: DOMRect }
  | { kind: 'aud'; r: DOMRect };

/**
 * L'ÉTAPE « À QUI », COLONNE DES CRITÈRES — la carte Période · Inclure · Sauf, le départ d'une audience enregistrée,
 * et l'enregistrement de celle-ci. Le compte et le groupe témoin vivent à côté, dans l'assistant.
 */
export function EtapeAudience({
  crit, onChange, audiences, audienceChoisie, onRepartir, ignores, enregistrer, onEnregistrer, nomAudience, onNomAudience,
}: {
  crit: Criteres;
  onChange: (c: Criteres) => void;
  audiences: SavedAudience[];
  /** L'audience dont on est reparti — son nom remplace l'invite du bouton. */
  audienceChoisie: SavedAudience | null;
  /** Repartir d'une audience, ou de zéro (`null`). */
  onRepartir: (a: SavedAudience | null) => void;
  ignores: string[];
  enregistrer: boolean;
  onEnregistrer: (on: boolean) => void;
  nomAudience: string;
  onNomAudience: (nom: string) => void;
}) {
  const [pop, setPop] = useState<Pop | null>(null);
  const [q, setQ] = useState('');
  const [segments, setSegments] = useState<string[] | null>(null);
  const [refusSegments, setRefusSegments] = useState<string | null>(null);
  const [facettes, setFacettes] = useState<ArticleFacets | null>(null);
  const [lieux, setLieux] = useState<{ rows: CampaignPlace[]; truncated: boolean } | null>(null);
  const [erreurChoix, setErreurChoix] = useState<string | null>(null);
  const fermer = () => { setPop(null); setQ(''); };
  const rect = (e: React.MouseEvent) => (e.currentTarget as HTMLElement).getBoundingClientRect();

  const enCours = pop?.kind === 'edit' ? crit[pop.zone][pop.i] ?? null : null;
  const defEnCours = enCours ? defDe(enCours.k) : null;

  // ⚠️ LES CHOIX SE LISENT À LA DEMANDE, au premier éditeur qui en a besoin : la liste des marques ou des villes
  //    n'a rien à faire dans l'assistant tant qu'on ne vise ni une marque ni une ville.
  // ⚠️ UNE LECTURE EN ÉCHEC SE DIT dans l'éditeur, avec son motif — une liste vide ferait croire que le magasin
  //    n'a aucune marque (§23.0).
  const src = defEnCours?.src;
  useEffect(() => {
    if (!src) return undefined;
    let vivant = true;
    const dire = (e: unknown) => { if (vivant) setErreurChoix(e instanceof Error ? e.message : String(e)); };
    setErreurChoix(null);
    if (src === 'seg' && segments === null && refusSegments === null) {
      api.campaignSegments()
        .then((r) => { if (vivant) setSegments(r.names); })
        .catch((e: unknown) => { if (vivant) setRefusSegments(e instanceof Error ? e.message : String(e)); });
    }
    if ((src === 'marq' || src === 'fam' || src === 'sup' || src === 'sais') && facettes === null) {
      api.articleFacets().then((r) => { if (vivant) setFacettes(r); }).catch(dire);
    }
    if (src === 'geo' && lieux === null) api.campaignPlaces().then((r) => { if (vivant) setLieux(r); }).catch(dire);
    return () => { vivant = false; };
  }, [src, segments, refusSegments, facettes, lieux]);

  const majBande = (zone: Zone, liste: Critere[]) => onChange({ ...crit, [zone]: liste });
  const poserValeur = (v: ValeurCritere) => {
    if (pop?.kind !== 'edit') return;
    const liste = crit[pop.zone].slice();
    liste[pop.i] = { ...liste[pop.i]!, v };
    majBande(pop.zone, liste);
  };
  const ajouter = (zone: Zone, d: DefCritere, r: DOMRect) => {
    // ⚠️ LA VALEUR DE DÉPART DU DESSIN : une plage « au moins 1 », une durée moyenne — posées d'emblée ; une liste
    //    ou un montant restent à compléter.
    const v0: ValeurCritere = d.ty === 'many' ? [] : d.ty === 'range' ? { min: '1', max: '' } : d.ty === 'date' ? d.quick![1]!.v : '';
    const liste = [...crit[zone], { k: d.k, v: v0 }];
    majBande(zone, liste);
    // ⚠️ CHOISIR UN CRITÈRE OUVRE TOUT DE SUITE SON ÉDITEUR — un clic de moins, et la valeur se saisit là où on regarde.
    setQ('');
    setPop({ kind: 'edit', zone, i: liste.length - 1, r });
  };
  const retirer = (zone: Zone, i: number) => {
    majBande(zone, crit[zone].filter((_, j) => j !== i));
    fermer();
  };

  const fenetre = fenetreDe(crit.per);
  const periodeServie = utilisePeriode(crit);
  const aCompleter = [...crit.inc, ...crit.exc].filter((x) => !estPose(x)).length;
  const posesInc = crit.inc.filter(estPose).length;
  const posesExc = crit.exc.filter(estPose).length;

  const pastilles = (zone: Zone): ReactNode => crit[zone].map((cr, i) => {
    const d = defDe(cr.k);
    const pose = estPose(cr);
    return (
      <span key={cr.k} style={{
        display: 'inline-flex', alignItems: 'center', height: 32, maxWidth: 340, borderRadius: 'var(--r-pill)', borderWidth: 1,
        boxSizing: 'border-box', ...teinte(pose, zone),
      }}>
        <button type="button" onClick={(e) => { setQ(''); setPop({ kind: 'edit', zone, i, r: rect(e) }); }} title={t('Modifier ce critère')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, height: '100%', minWidth: 0, padding: '0 5px 0 11px', border: 'none',
            background: 'transparent', color: 'inherit', fontSize: 'var(--t-dense)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
          }}>
          <Icon name={d.ic} size={13} stroke={2} style={{ flexShrink: 0 }} />
          {/* ⚠️ LA PHRASE DES PASTILLES DE FILTRE DES STATISTIQUES — une seule clé pour la même forme. */}
          <Trans k='<1>{critere} :</1> <2>{valeurs}</2>' vars={{ critere: d.s(), valeurs: pose ? valeurLisible(cr) : t('à compléter') }} parts={[
            (x) => <span style={{ fontWeight: 600, opacity: 0.8, flexShrink: 0 }}>{x}</span>,
            (x) => <strong style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{x}</strong>,
          ]} />
        </button>
        <button type="button" onClick={() => retirer(zone, i)} title={t('Retirer ce critère')}
          aria-label={t('Retirer le critère « {critere} »', { critere: d.s() })}
          style={{
            width: 26, height: '100%', flexShrink: 0, border: 'none', borderRadius: '0 var(--r-pill) var(--r-pill) 0',
            background: 'transparent', color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>
          <Icon name="x" size={11} stroke={2.6} />
        </button>
      </span>
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={(e) => setPop({ kind: 'aud', r: rect(e) })}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, maxWidth: '100%', padding: '0 12px',
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-800)',
            fontSize: 'var(--t-body)', fontWeight: 600, cursor: 'pointer',
          }}>
          <Icon name="save" size={14} stroke={1.9} color="var(--teal-700)" style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {audienceChoisie ? audienceChoisie.name : t('Repartir d’une audience enregistrée')}
          </span>
          <Icon name="chevronD" size={12} stroke={2.2} style={{ flexShrink: 0, opacity: 0.55 }} />
        </button>
        <span style={{ flex: 1, minWidth: 180, fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.4 }}>
          {t('On garde les critères, jamais la liste : une audience se recalcule à chaque usage.')}
        </span>
      </div>
      {/* ⚠️ 🔴 CE QU'ON N'A PAS SU AFFICHER SE DIT. Une audience qui porte un critère que cet écran ne rend pas, rechargée
          puis figée ici, partirait PLUS LARGE que ce qui avait été enregistré — sans qu'aucun écran ne mente visiblement. */}
      {ignores.length > 0 && (
        <span role="alert" style={AMBRE}>
          {t('Cette audience contient des critères que cet écran ne sait pas afficher ({v}). Si vous la figez ainsi, ils seront PERDUS et la campagne partira plus large.', { v: ignores.join(', ') })}
        </span>
      )}

      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)' }}>
        <div style={{ ...BANDE, borderBottom: '1px solid var(--ink-100)' }}>
          <span style={BANDE_NOM}>{t('Période')}</span>
          {/* ⚠️ EN RETRAIT QUAND RIEN NE LA LIT : palier, anniversaire, ville… l'ignorent, et une période vive à côté
              d'eux ferait croire qu'elle les borne. */}
          <button type="button" onClick={(e) => setPop({ kind: 'per', r: rect(e) })}
            style={{
              opacity: periodeServie ? 1 : 0.55, display: 'inline-flex', alignItems: 'center', gap: 8, height: 34, padding: '0 11px',
              border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)',
              fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', cursor: 'pointer',
            }}>
            <Icon name="calendar" size={14} stroke={1.9} color="var(--teal-700)" style={{ flexShrink: 0 }} />
            {nomPeriode(crit.per)}
            <span style={{ fontWeight: 500, color: 'var(--ink-500)' }}>{fenetre ? plageLisible(fenetre) : t('sans borne')}</span>
            <Icon name="chevronD" size={12} stroke={2.2} style={{ flexShrink: 0, opacity: 0.55 }} />
          </button>
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
            {periodeServie
              ? t('pour les achats, le panier et les visites')
              : t('aucun critère posé ne l’utilise — palier, anniversaire, ville… l’ignorent')}
          </span>
        </div>
        <div style={{ ...BANDE, borderBottom: '1px solid var(--ink-100)' }}>
          <span style={BANDE_NOM}>{t('Inclure')}</span>
          {!crit.inc.length && <span style={VIDE}>{t('Tous les clients joignables du magasin')}</span>}
          {pastilles('inc')}
          <button type="button" className="oz-stats-dashed" onClick={(e) => setPop({ kind: 'add', zone: 'inc', r: rect(e) })} style={AJOUT}>
            <Icon name="plus" size={12} stroke={2.6} />{t('Critère')}
          </button>
          {posesInc > 1 && <span style={REGLE}>{t('tous à la fois')}</span>}
        </div>
        <div style={BANDE}>
          <span style={BANDE_NOM}>{t('Sauf')}</span>
          {!crit.exc.length && <span style={VIDE}>{t('Personne n’est écarté')}</span>}
          {pastilles('exc')}
          <button type="button" className="oz-stats-dashed" onClick={(e) => setPop({ kind: 'add', zone: 'exc', r: rect(e) })} style={AJOUT}>
            <Icon name="plus" size={12} stroke={2.6} />{t('Exclusion')}
          </button>
          {posesExc > 0 && <span style={REGLE}>{t('un seul suffit pour être écarté')}</span>}
        </div>
      </div>

      {aCompleter > 0 && (
        <div role="note" style={AMBRE}>
          <Icon name="alert" size={14} stroke={2.1} style={{ flexShrink: 0 }} />
          <span>{t('{n} critère à compléter — il ne filtre pas tant qu’il n’a pas de valeur.', { n: aCompleter })}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Switch checked={enregistrer} onChange={onEnregistrer} label={t('Enregistrer l’audience')} />
        {enregistrer && (
          <input value={nomAudience} onChange={(e) => onNomAudience(e.target.value)} maxLength={60}
            aria-label={t('Nom de l’audience')} placeholder={t('Nom de l’audience — ex. Gold dormants')}
            style={{ ...CHAMP, flex: 1, minWidth: 200, width: 'auto' }} />
        )}
      </div>
      {enregistrer && (
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: -6 }}>
          {/* ⚠️ ON ANNONCE LE REMPLACEMENT AVANT, pas après : réenregistrer sous un nom existant écrase, et
              l'exploitant doit le savoir pendant qu'il peut encore changer de nom. */}
          {!nomAudience.trim()
            ? t('Donnez-lui un nom : c’est lui que vous relirez dans la liste des audiences.')
            : audiences.some((a) => a.name === nomAudience.trim())
              ? t('Ce nom existe déjà : l’audience enregistrée sera REMPLACÉE par celle-ci.')
              : t('Les critères seront gardés et rejoués à chaque réutilisation — jamais la liste des clients d’aujourd’hui.')}
        </span>
      )}

      {pop?.kind === 'add' && (
        <StatsPopover r={pop.r} width={290} onClose={fermer}>
          <div style={{ ...POP_HEAD, padding: '11px 13px 2px' }}>{pop.zone === 'exc' ? t('Écarter ceux qui…') : t('Ajouter un critère')}</div>
          <div style={{ padding: '0 6px 8px' }}>
            {GROUPES.map((g) => (
              <div key={g.g}>
                <div style={{ padding: '9px 9px 3px', fontSize: 'var(--t-micro)', fontWeight: 700, color: 'var(--ink-400)' }}>{t(g.l)}</div>
                {CRITERES.filter((d) => d.g === g.g).map((d) => {
                  // ⚠️ UN CRITÈRE NE SE POSE QU'UNE FOIS PAR BANDE — il peut figurer dans les deux (« Gold et plus » sauf
                  //    « Platinium »).
                  const deja = crit[pop.zone].some((x) => x.k === d.k);
                  return (
                    <button key={d.k} type="button" disabled={deja} onClick={() => ajouter(pop.zone, d, pop.r)} className="oz-stats-menu-row"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 34, padding: '0 9px', border: 'none',
                        borderRadius: 'var(--r-xs)', background: 'transparent', textAlign: 'left', cursor: deja ? 'default' : 'pointer',
                        opacity: deja ? 0.45 : 1,
                      }}>
                      <Icon name={d.ic} size={15} stroke={1.8} color="var(--teal-700)" style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.l()}</span>
                      {d.per && !deja && <span title={t('Lit la période observée')} style={{ display: 'inline-flex' }}><Icon name="calendar" size={12} stroke={2} color="var(--ink-400)" style={{ flexShrink: 0 }} /></span>}
                      {deja && <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t('déjà posé')}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </StatsPopover>
      )}

      {pop?.kind === 'edit' && enCours && defEnCours && (
        <StatsPopover r={pop.r} width={300} onClose={fermer}>
          <Editeur def={defEnCours} cr={enCours} zone={pop.zone} per={crit.per} q={q} onQ={setQ} onValeur={poserValeur} onFermer={fermer}
            sources={{ segments, facettes, lieux: lieux?.rows ?? null }} refusSegments={refusSegments} erreurChoix={erreurChoix}
            tronque={defEnCours.src === 'geo' ? Boolean(lieux?.truncated) : defEnCours.src && facettes && defEnCours.src in facettes.truncated
              ? Boolean(facettes.truncated[defEnCours.src as keyof ArticleFacets['truncated']]) : false} />
        </StatsPopover>
      )}

      {pop?.kind === 'per' && (
        <StatsPopover r={pop.r} width={330} onClose={fermer}>
          <div style={POP_HEAD}>{t('Période observée')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, padding: '6px 10px 10px' }}>
            {PERIODES.map((p) => (
              <button key={p.q} type="button" aria-pressed={'q' in crit.per && crit.per.q === p.q}
                onClick={() => onChange({ ...crit, per: { q: p.q } })} style={choix('q' in crit.per && crit.per.q === p.q)}>
                {t(p.l)}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', padding: '11px 10px', borderTop: '1px solid var(--ink-100)' }}>
            {(['f', 't'] as const).map((bord) => {
              const iso = fenetre ? versIso(bord === 'f' ? fenetre.from : fenetre.to) : '';
              return (
                <label key={bord} style={{ flex: 1, minWidth: 0 }}>
                  <span style={SOUS_TITRE}>{bord === 'f' ? t('Du') : t('Au')}</span>
                  <input type="date" value={iso}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      // ⚠️ DES BORNES DANS LE BON ORDRE : un « du » après le « au » emporte l'autre borne avec lui.
                      const f = bord === 'f' ? v : versIso(fenetre?.from ?? '') || v;
                      const to = bord === 't' ? v : versIso(fenetre?.to ?? '') || v;
                      onChange({ ...crit, per: bord === 'f' ? { f, t: to < f ? f : to } : { f: f > to ? to : f, t: to } });
                    }}
                    style={{ ...CHAMP, height: 36, padding: '0 8px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)' }} />
                </label>
              );
            })}
          </div>
          <div style={PIED}>
            <span style={{ minWidth: 0, fontSize: 'var(--t-note)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {fenetre
                ? t('{n} jour · {plage}', { n: joursEntre(fenetre.from, fenetre.to), plage: plageLisible(fenetre) })
                : t('Aucune borne : tous les achats depuis la création de la fiche')}
            </span>
            <button type="button" onClick={fermer} style={FERMER}>{t('Fermer')}</button>
          </div>
        </StatsPopover>
      )}

      {pop?.kind === 'aud' && (
        <StatsPopover r={pop.r} width={300} onClose={fermer}>
          <div style={POP_HEAD}>{t('Audiences enregistrées')}</div>
          {!audiences.length && (
            <div style={{ padding: '12px 13px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Aucune audience enregistrée pour l’instant.')}</div>
          )}
          <div style={{ maxHeight: 300, overflowY: 'auto', padding: '2px 6px 6px' }}>
            {audiences.map((a) => {
              const on = audienceChoisie?.id === a.id;
              return (
                <button key={a.id} type="button" onClick={() => { onRepartir(a); fermer(); }} className="oz-stats-menu-row"
                  style={{
                    display: 'block', width: '100%', padding: '8px 10px', border: 'none', borderRadius: 'var(--r-xs)',
                    background: on ? 'var(--teal-100)' : 'transparent', textAlign: 'left', cursor: 'pointer',
                  }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: on ? 'var(--teal-800)' : 'var(--ink-900)' }}>{a.name}</span>
                  <span style={{ display: 'block', marginTop: 1, fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.4 }}>
                    {phraseAudience(criteresDe(a.filters).criteres)}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={PIED}>
            <button type="button" onClick={() => { onRepartir(null); fermer(); }} style={DISCRET}>{t('Repartir de zéro')}</button>
            <button type="button" onClick={fermer} style={FERMER}>{t('Fermer')}</button>
          </div>
        </StatsPopover>
      )}
    </div>
  );
}

/** Le nombre de jours d'une fenêtre, bornes comprises. */
const joursEntre = (from: string, to: string): number => {
  const jour = (ymd: string) => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));
  return Math.round((jour(to) - jour(from)) / 86_400_000) + 1;
};

/** L'éditeur d'un critère posé, dans le popover de sa pastille. */
function Editeur({
  def, cr, zone, per, q, onQ, onValeur, onFermer, sources, refusSegments, erreurChoix, tronque,
}: {
  def: DefCritere; cr: Critere; zone: Zone; per: Periode; q: string; onQ: (q: string) => void;
  onValeur: (v: ValeurCritere) => void; onFermer: () => void; sources: SourcesDeChoix;
  refusSegments: string | null; erreurChoix: string | null; tronque: boolean;
}) {
  const liste = def.ty === 'many' || def.ty === 'one';
  const toutes = liste ? optionsDe(def, sources) : [];
  const plier = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const montrees = toutes.filter(([, l]) => !q || plier(l).includes(plier(q)));
  const choisies = def.ty === 'many' ? (Array.isArray(cr.v) ? cr.v : []) : [typeof cr.v === 'string' ? cr.v : ''];
  const fenetre = fenetreDe(per);
  const note = def.note
    ? t(def.note)
    : def.per
      ? (fenetre
        ? t('Compté sur la période observée : {periode} ({plage}).', { periode: nomPeriode(per).toLocaleLowerCase(currentLang()), plage: plageLisible(fenetre) })
        : t('Compté sur toute la vie du client.'))
      : null;
  const chargement = liste && def.src !== 'pal' && def.src !== 'tit' && def.src !== 'mois'
    && (def.src === 'seg' ? sources.segments === null && !refusSegments : def.src === 'geo' ? sources.lieux === null : sources.facettes === null)
    && !erreurChoix;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 13px 6px' }}>
        <span style={{ ...POP_HEAD, padding: 0, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{def.l()}</span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', flexShrink: 0,
          background: zone === 'exc' ? 'var(--rose-bg)' : 'var(--teal-100)', color: zone === 'exc' ? 'var(--rose-fg)' : 'var(--teal-800)',
          fontSize: 'var(--t-micro)', fontWeight: 700,
        }}>{zone === 'exc' ? t('Écarte') : t('Inclut')}</span>
      </div>

      {liste && toutes.length > 8 && (
        <div style={{ padding: '0 8px 6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', background: 'var(--ink-50)', color: 'var(--ink-400)' }}>
            <Icon name="search" size={14} stroke={2} style={{ flexShrink: 0 }} />
            <input value={q} onChange={(e) => onQ(e.target.value)} autoFocus placeholder={t('Rechercher…')} aria-label={def.l()}
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: 'var(--t-body)', color: 'var(--ink-900)' }} />
          </div>
        </div>
      )}
      {liste && (
        <div style={{ maxHeight: 264, overflowY: 'auto', padding: '2px 6px 6px' }}>
          {def.src === 'seg' && refusSegments && (
            <div role="alert" style={{ padding: '10px', fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', lineHeight: 1.45 }}>{refusSegments}</div>
          )}
          {erreurChoix && (
            <div role="alert" style={{ padding: '10px', fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', lineHeight: 1.45 }}>
              {t('Liste illisible : {v}', { v: erreurChoix })}
            </div>
          )}
          {chargement && <div style={{ padding: '14px 10px', fontSize: 'var(--t-body)', color: 'var(--ink-400)', textAlign: 'center' }}>{t('Chargement…')}</div>}
          {montrees.map(([v, l]) => {
            const on = choisies.includes(v);
            return (
              <button key={v} type="button" role={def.ty === 'many' ? 'checkbox' : 'radio'} aria-checked={on} className="oz-stats-pick-row"
                onClick={() => {
                  if (def.ty === 'one') { onValeur(v); onFermer(); return; }
                  onValeur(on ? choisies.filter((x) => x !== v) : [...choisies, v]);
                }}
                style={{
                  display: 'block', width: '100%', height: 32, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)',
                  background: on && def.ty === 'one' ? 'var(--teal-100)' : 'transparent', color: on ? 'var(--teal-800)' : 'var(--ink-800)',
                  fontSize: 'var(--t-body)', fontWeight: on ? 700 : 500, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden',
                  textOverflow: 'ellipsis', cursor: 'pointer',
                }}>
                {def.ty === 'many' && (
                  <span style={{
                    width: 16, height: 16, borderRadius: 'var(--r-xs)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`,
                    background: on ? 'var(--teal-500)' : 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, boxSizing: 'border-box', marginRight: 8, verticalAlign: -3,
                  }}>{on && <Icon name="check" size={10} stroke={3.4} color="#fff" />}</span>
                )}
                {l}
              </button>
            );
          })}
          {!chargement && !montrees.length && !refusSegments && !erreurChoix && (
            <div style={{ padding: '14px 10px', fontSize: 'var(--t-body)', color: 'var(--ink-400)', textAlign: 'center' }}>{t('Aucun résultat')}</div>
          )}
          {/* ⚠️ UNE LISTE TRONQUÉE LE DIT : une valeur absente ne se choisirait pas, et on croirait qu'elle n'existe pas. */}
          {tronque && <div style={{ padding: '6px 10px', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Liste tronquée : affinez la recherche.')}</div>}
        </div>
      )}

      {def.ty === 'eur' && (
        <div style={{ padding: '4px 13px 6px' }}>
          <span style={{ position: 'relative', display: 'block' }}>
            <input value={typeof cr.v === 'string' ? cr.v : ''} inputMode="decimal" autoFocus placeholder="0" aria-label={def.l()}
              onChange={(e) => onValeur(e.target.value.replace(/[^\d,.]/g, '').slice(0, 9))}
              style={{ ...CHAMP, paddingRight: 32, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} />
            <span style={{ position: 'absolute', right: 12, top: 10, fontSize: 'var(--t-body)', color: 'var(--ink-500)', pointerEvents: 'none' }}>
              {currentCurrency().symbol}
            </span>
          </span>
        </div>
      )}

      {def.ty === 'range' && (
        <div style={{ display: 'flex', gap: 10, padding: '4px 13px 6px' }}>
          {(['min', 'max'] as const).map((borne) => (
            <label key={borne} style={{ flex: 1, minWidth: 0 }}>
              <span style={SOUS_TITRE}>{borne === 'min' ? t('Au moins') : t('Au plus')}</span>
              <input value={plageDe(cr.v)[borne]} inputMode="numeric" autoFocus={borne === 'min'}
                placeholder={borne === 'min' ? '—' : t('sans limite')}
                onChange={(e) => onValeur({ ...plageDe(cr.v), [borne]: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                style={{ ...CHAMP, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} />
            </label>
          ))}
        </div>
      )}

      {def.ty === 'date' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5, padding: '4px 13px 8px' }}>
            {(def.quick ?? []).map((x) => (
              <button key={x.v} type="button" aria-pressed={cr.v === x.v} onClick={() => onValeur(x.v)} style={choix(cr.v === x.v)}>{t(x.l)}</button>
            ))}
          </div>
          <label style={{ display: 'block', padding: '0 13px 6px' }}>
            <span style={SOUS_TITRE}>{def.dl ? t(def.dl) : ''}</span>
            <input type="date" value={typeof cr.v === 'string' && estIso(cr.v) ? cr.v : typeof cr.v === 'string' && estDuree(cr.v) ? versIso(dateDeDuree(cr.v)) : ''}
              onChange={(e) => { if (e.target.value) onValeur(e.target.value); }}
              style={{ ...CHAMP, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)' }} />
          </label>
        </>
      )}

      {note && <div style={{ padding: '2px 13px 10px', fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45 }}>{note}</div>}

      <div style={PIED}>
        {def.ty === 'many' && (
          <>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{t('{n} sélectionné', { n: choisies.length })}</span>
            <button type="button" onClick={() => onValeur([])} style={DISCRET}>{t('Tout décocher')}</button>
          </>
        )}
        <button type="button" onClick={onFermer} style={FERMER}>{t('Fermer')}</button>
      </div>
    </>
  );
}

/** La date qu'une durée donne aujourd'hui — pour montrer ce que « 6 mois » veut dire au calendrier. */
const dateDeDuree = (s: CampaignRelativeSpan): string => monthsBefore(aujourdhui(), spanMonths(s));
